import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Modal, ScrollArea } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getPaymentChannelList, deletePaymentChannel } from '../../../../services/payment-channels'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TablePaymentChannels from '../../../../component/pages/PaymentChannels/TablePaymentChannels'
import FormPaymentChannels from '../../../../component/pages/PaymentChannels/FormPaymentChannels'
import { modals } from '@mantine/modals'
import { notificationSuccess, notificationError } from '../../../../component/ui/Notifications'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
}

const labelTable = [
    { label: 'ID', width: 50 },
    { label: 'Name', width: undefined },
    { label: 'Payment Type', width: 250 },
    { label: 'Action', width: 100 },
]

const PaymentChannelsList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [paymentChannelsList, setPaymentChannelsList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Common & Global Settings', route: '/settings' },
        { label: 'Payment Channels', route: null },
    ]

    const handleGetAllPaymentChannels = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getPaymentChannelList(params)
            if (response.data) {
                const dataPaymentChannels = response.data
                const countData = response.meta?.pagination?.total || dataPaymentChannels.length

                const remapPaymentChannels = dataPaymentChannels.map((val) => {
                    return {
                        id: val.id,
                        name: val.attributes?.name,
                        virtual_account: val.attributes?.virtual_account,
                        payment_link: val.attributes?.payment_link,
                        apikey: val.attributes?.apikey,
                        instruction: val.attributes?.instruction,
                        gateway: val.attributes?.gateway,
                        logo_url: val.attributes?.logo_url,
                    }
                })

                setPaymentChannelsList(remapPaymentChannels)
                setTotalData(countData)
            } else {
                setPaymentChannelsList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setPaymentChannelsList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
        // eslint-disable-next-line 
    }, [dispatch])

    useEffect(() => {
        handleGetAllPaymentChannels()
        // eslint-disable-next-line
    }, [params])

    const handleDelete = (id) => {
        modals.openConfirmModal({
            title: 'Delete Payment Channel',
            centered: true,
            children: (
                <Text size="sm">
                    Are you sure you want to delete this payment channel? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: "Cancel" },
            confirmProps: { color: 'red', size: 'xs' },
            cancelProps: { size: 'xs' },
            onConfirm: async () => {
                try {
                    await deletePaymentChannel(id)
                    notificationSuccess('Success', 'Payment Channel has been deleted')
                    handleGetAllPaymentChannels()
                } catch (error) {
                    notificationError('Error', 'Failed to delete payment channel')
                }
            },
        });
    }

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        delete: (val) => {
            handleDelete(val.id)
        }
    }

    const handleAction = (val, type) => {
        return actions[type](val)
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        // eslint-disable-next-line
        setParams((oldVal) => ({ ...oldVal, ['page[num]']: val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const onAddNew = () => {
        setDetailData(null)
        setOpenModalForm(true)
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Payment Channels</Text>
                        <Group gap='sm' align='center'>
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' variant="filled" onClick={onAddNew}>Add New</Button>
                        </Group>
                    </Flex>
                </Box>
                
                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data: </Text>
                        <Text fz={12} fw='bold'>{totalData} </Text>
                    </Group>
                    {loading ? <LoadingData /> : paymentChannelsList.length > 0 ? <TablePaymentChannels label={labelTable} data={paymentChannelsList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>
            
            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>{detailData ? 'Edit' : 'Add'} Payment Channel</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormPaymentChannels dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllPaymentChannels} />
            </Modal>
        </AuthLayout>
    )
}

export default PaymentChannelsList
